// src/tabs/Users.jsx
// Benutzerverwaltung - deckt die AuthEndpoints ab:
//   GET  /api/auth/users             (nur Admin - Liste aller Benutzer)
//   POST /api/auth/register          (nur Admin - neuen Benutzer anlegen)
//   PUT  /api/auth/users/{id}        (nur Admin - Email/Passwort eines Benutzers ändern)
//   DELETE /api/auth/users/{id}      (nur Admin - Benutzer löschen)
//   POST /api/auth/change-password   (jeder eingeloggte Benutzer - eigenes Passwort)
//
// Die mittlere Karte ("Neuen Benutzer anlegen") wird beim Klick auf das Bearbeiten-Symbol in
// der Liste zu einer Bearbeiten-Karte umgeschaltet (bearbeitungsBenutzer !== null). Speichern
// und Abbrechen setzen bearbeitungsBenutzer danach immer wieder auf null zurück - die Karte
// zeigt anschließend wieder "Neu anlegen" mit leeren Feldern.
import React, { useCallback, useEffect, useState } from 'react'
import api from '../api'
import { useAuth } from '../authContext'
import { useDialog } from '../components/shared/Dialog'
import LoadingSpinner from '../components/shared/LoadingSpinner'

// Liest eine Fehlermeldung aus einer axios-Exception, unabhängig davon, ob das
// Backend einen reinen String (Results.BadRequest/NotFound) oder ein Objekt
// mit "error"/"title" (Results.Json/ProblemDetails) zurückgegeben hat.
function extractErrorMessage (err, fallback) {
  if (err.response) {
    const data = err.response.data
    if (typeof data === 'string' && data.trim() !== '') return data
    if (data?.error) return data.error
    if (data?.title) return data.title
    return fallback
  }
  return 'Netzwerkfehler. Bitte versuche es später erneut.'
}

// Backend liefert CreatedAt als Iso-DateTime-String.
function formatErstelltAm (value) {
  if (!value) return '–'
  const datum = new Date(value)
  if (Number.isNaN(datum.getTime())) return String(value)
  return datum.toLocaleDateString('de-DE')
}

// Nur für Rolle "Tester" gesetzt (sonst dokumentKontingent === null -> unbegrenzt).
function formatKontingent (benutzer) {
  if (benutzer.dokumentKontingent == null) return '–'
  return `${benutzer.dokumentVerbraucht} / ${benutzer.dokumentKontingent}`
}

// Passwort-Eingabefeld mit Sichtbarkeits-Toggle (gleiches Muster wie LoginForm.jsx).
function PasswordField ({ id, label, value, onChange, required, minLength }) {
  const [visible, setVisible] = useState(false)

  return (
    <div className='form-field'>
      <label htmlFor={id}>{label}</label>
      <div className='password-input-wrapper'>
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          required={required}
          minLength={minLength}
        />
        <button
          type='button'
          className='password-toggle-btn'
          onClick={() => setVisible(v => !v)}
          tabIndex={-1}
          aria-label={visible ? 'Passwort verbergen' : 'Passwort anzeigen'}
        >
          {visible ? '👁️' : '🔒'}
        </button>
      </div>
    </div>
  )
}

// ===== Liste aller Benutzer (linke Karte) =====
function UserListe ({
  users,
  loading,
  error,
  bearbeitungsId,
  eigenerBenutzerId,
  onBearbeiten,
  onLoeschen
}) {
  const { confirm } = useDialog()

  const loeschenMitBestaetigung = async benutzer => {
    const bestaetigt = await confirm({
      titel: 'Benutzer löschen',
      nachricht: `Benutzer "${benutzer.username}" wirklich löschen? Das kann nicht rückgängig gemacht werden.`,
      bestaetigenLabel: 'Löschen',
      gefahr: true
    })
    if (bestaetigt) {
      onLoeschen(benutzer.id)
    }
  }

  return (
    <div className='user-form-card user-liste-card'>
      <h3>Benutzer</h3>
      {error && <p className='error-banner'>{error}</p>}
      {loading ? (
        <LoadingSpinner label='Benutzer werden geladen…' />
      ) : users.length === 0 ? (
        <p className='placeholder-tab'>Keine Benutzer vorhanden.</p>
      ) : (
        <table className='users-tabelle'>
          <thead>
            <tr>
              <th>Benutzername</th>
              <th>E-Mail</th>
              <th>Rolle</th>
              <th>Kontingent</th>
              <th>Status</th>
              <th>Erstellt</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map(benutzer => {
              const istEigenerBenutzer = benutzer.id === eigenerBenutzerId
              return (
                <tr
                  key={benutzer.id}
                  className={
                    benutzer.id === bearbeitungsId ? 'zeile-aktiv' : undefined
                  }
                >
                  <td>{benutzer.username}</td>
                  <td>{benutzer.email}</td>
                  <td>{benutzer.role}</td>
                  <td>{formatKontingent(benutzer)}</td>
                  <td>
                    <span
                      className={`status-badge ${
                        benutzer.active ? 'status-ok' : 'status-unbekannt'
                      }`}
                    >
                      {benutzer.active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td>{formatErstelltAm(benutzer.createdAt)}</td>
                  <td className='users-tabelle-aktionen'>
                    <button
                      type='button'
                      className='btn-icon'
                      title='Benutzer bearbeiten'
                      onClick={() => onBearbeiten(benutzer)}
                    >
                      ✏️
                    </button>
                    <button
                      type='button'
                      className='btn-icon'
                      title={
                        istEigenerBenutzer
                          ? 'Der eigene Benutzer kann nicht gelöscht werden'
                          : 'Benutzer löschen'
                      }
                      disabled={istEigenerBenutzer}
                      onClick={() => loeschenMitBestaetigung(benutzer)}
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ===== Anlegen/Bearbeiten (mittlere Karte) =====
function UserFormCard ({
  bearbeitungsBenutzer,
  onCreated,
  onUpdated,
  onAbbrechen
}) {
  const { token } = useAuth()
  const bearbeitungsModus = bearbeitungsBenutzer !== null

  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState('User')
  const [kontingent, setKontingent] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // Nur bei Rolle "Tester" gibt es überhaupt ein Kontingent-Feld - im Anlegen-Modus abhängig
  // von der gewählten Rolle, im Bearbeiten-Modus abhängig von der (fixen) Rolle des Benutzers.
  const zeigeKontingentFeld = bearbeitungsModus
    ? bearbeitungsBenutzer.role === 'Tester'
    : role === 'Tester'

  // Beim Wechsel zwischen "Neu anlegen" und "Bearbeiten" (oder zurück) Felder befüllen/leeren.
  useEffect(() => {
    setError(null)
    setSuccess(null)
    setPassword('')
    if (bearbeitungsBenutzer) {
      setEmail(bearbeitungsBenutzer.email)
      setKontingent(
        bearbeitungsBenutzer.role === 'Tester' &&
          bearbeitungsBenutzer.dokumentKontingent != null
          ? String(bearbeitungsBenutzer.dokumentKontingent)
          : ''
      )
    } else {
      setUsername('')
      setEmail('')
      setRole('User')
      setKontingent('')
    }
  }, [bearbeitungsBenutzer])

  const handleSubmit = async e => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    setLoading(true)

    try {
      if (bearbeitungsModus) {
        const body = { email, newPassword: password || null }
        if (zeigeKontingentFeld) {
          body.dokumentKontingent =
            kontingent === '' ? null : Number(kontingent)
        }
        const response = await api.put(
          `/auth/users/${bearbeitungsBenutzer.id}`,
          body,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        onUpdated(response.data)
      } else {
        const body = { username, password, email, role }
        if (zeigeKontingentFeld) {
          body.dokumentKontingent =
            kontingent === '' ? null : Number(kontingent)
        }
        const response = await api.post('/auth/register', body, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setSuccess(
          `Benutzer "${username}" wurde angelegt (ID ${response.data.id}).`
        )
        setUsername('')
        setPassword('')
        setEmail('')
        setRole('User')
        setKontingent('')
        onCreated()
      }
    } catch (err) {
      setError(
        extractErrorMessage(
          err,
          bearbeitungsModus
            ? 'Speichern fehlgeschlagen.'
            : 'Anlegen des Benutzers fehlgeschlagen.'
        )
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='user-form-card'>
      <h3>
        {bearbeitungsModus
          ? `Benutzer "${bearbeitungsBenutzer.username}" bearbeiten`
          : 'Neuen Benutzer anlegen'}
      </h3>
      {error && <p className='error-banner'>{error}</p>}
      {success && <p className='success-banner'>{success}</p>}
      <form onSubmit={handleSubmit}>
        {!bearbeitungsModus && (
          <div className='form-field'>
            <label htmlFor='new-username'>Benutzername</label>
            <input
              id='new-username'
              type='text'
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
            />
          </div>
        )}
        <div className='form-field'>
          <label htmlFor='user-email'>E-Mail</label>
          <input
            id='user-email'
            type='email'
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
        </div>
        <PasswordField
          id='user-password'
          label={bearbeitungsModus ? 'Neues Passwort (optional)' : 'Passwort'}
          value={password}
          onChange={e => setPassword(e.target.value)}
          required={!bearbeitungsModus}
          minLength={8}
        />
        {!bearbeitungsModus && (
          <div className='form-field'>
            <label htmlFor='new-role'>Rolle</label>
            <select
              id='new-role'
              value={role}
              onChange={e => setRole(e.target.value)}
            >
              <option value='User'>User</option>
              <option value='Admin'>Admin</option>
              <option value='Tester'>Tester</option>
            </select>
          </div>
        )}
        {zeigeKontingentFeld && (
          <div className='form-field'>
            <label htmlFor='user-kontingent'>Dokumenten-Kontingent</label>
            <input
              id='user-kontingent'
              type='number'
              min={bearbeitungsModus ? 0 : 1}
              value={kontingent}
              onChange={e => setKontingent(e.target.value)}
              required={!bearbeitungsModus}
            />
            {bearbeitungsModus && (
              <p className='form-hint'>
                Bereits verbraucht: {bearbeitungsBenutzer.dokumentVerbraucht}
              </p>
            )}
          </div>
        )}
        <div className='form-aktionen'>
          <button type='submit' className='btn btn-primary' disabled={loading}>
            {loading
              ? bearbeitungsModus
                ? 'Wird gespeichert…'
                : 'Wird angelegt…'
              : bearbeitungsModus
              ? 'Speichern'
              : 'Benutzer anlegen'}
          </button>
          {bearbeitungsModus && (
            <button
              type='button'
              className='btn'
              onClick={onAbbrechen}
              disabled={loading}
            >
              Abbrechen
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

// ===== Eigenes Passwort ändern (rechte Karte) =====
function ChangePasswordForm () {
  const { token } = useAuth()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  const handleSubmit = async e => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (newPassword !== confirmPassword) {
      setError('Die Wiederholung stimmt nicht mit dem neuen Passwort überein.')
      return
    }

    setLoading(true)
    try {
      await api.post(
        '/auth/change-password',
        { currentPassword, newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setSuccess('Passwort wurde geändert.')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      setError(extractErrorMessage(err, 'Ändern des Passworts fehlgeschlagen.'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='user-form-card'>
      <h3>Eigenes Passwort ändern</h3>
      {error && <p className='error-banner'>{error}</p>}
      {success && <p className='success-banner'>{success}</p>}
      <form onSubmit={handleSubmit}>
        <PasswordField
          id='current-password'
          label='Aktuelles Passwort'
          value={currentPassword}
          onChange={e => setCurrentPassword(e.target.value)}
          required
        />
        <PasswordField
          id='change-new-password'
          label='Neues Passwort'
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
          required
          minLength={8}
        />
        <PasswordField
          id='confirm-password'
          label='Neues Passwort wiederholen'
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          required
          minLength={8}
        />
        <button type='submit' className='btn btn-primary' disabled={loading}>
          {loading ? 'Wird geändert…' : 'Passwort ändern'}
        </button>
      </form>
    </div>
  )
}

function Users () {
  const { token, userId } = useAuth()
  const [users, setUsers] = useState([])
  const [listLoading, setListLoading] = useState(true)
  const [listError, setListError] = useState(null)
  // null = Karte zeigt "Neu anlegen", sonst der gerade bearbeitete Benutzer-Datensatz.
  const [bearbeitungsBenutzer, setBearbeitungsBenutzer] = useState(null)

  const ladeBenutzer = useCallback(async () => {
    setListLoading(true)
    setListError(null)
    try {
      const response = await api.get('/auth/users', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setUsers(response.data)
    } catch (err) {
      setListError(
        extractErrorMessage(err, 'Benutzerliste konnte nicht geladen werden.')
      )
    } finally {
      setListLoading(false)
    }
  }, [token])

  useEffect(() => {
    ladeBenutzer()
  }, [ladeBenutzer])

  // /register liefert nur { id } zurück - für die Liste reicht ein einfaches Neuladen.
  const handleCreated = () => {
    ladeBenutzer()
  }

  // /users/{id} (PUT) liefert das vollständige, aktualisierte UserListItem zurück.
  const handleUpdated = aktualisierterBenutzer => {
    setUsers(vorherige =>
      vorherige.map(u =>
        u.id === aktualisierterBenutzer.id ? aktualisierterBenutzer : u
      )
    )
    setBearbeitungsBenutzer(null)
  }

  const handleLoeschen = async id => {
    try {
      await api.delete(`/auth/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setUsers(vorherige => vorherige.filter(u => u.id !== id))
      if (bearbeitungsBenutzer?.id === id) {
        setBearbeitungsBenutzer(null)
      }
    } catch (err) {
      setListError(
        extractErrorMessage(err, 'Löschen des Benutzers fehlgeschlagen.')
      )
    }
  }

  return (
    <div className='tab-content'>
      <div className='users-tab-sections'>
        <UserListe
          users={users}
          loading={listLoading}
          error={listError}
          bearbeitungsId={bearbeitungsBenutzer?.id ?? null}
          eigenerBenutzerId={userId}
          onBearbeiten={setBearbeitungsBenutzer}
          onLoeschen={handleLoeschen}
        />
        <UserFormCard
          bearbeitungsBenutzer={bearbeitungsBenutzer}
          onCreated={handleCreated}
          onUpdated={handleUpdated}
          onAbbrechen={() => setBearbeitungsBenutzer(null)}
        />
        <ChangePasswordForm />
      </div>
    </div>
  )
}

export default Users
